// Phase II · Wave D.1 · LocaleSwitcher · monochrome V12 Achromatic Strict.
//
// Dropdown with the current locale label (native name) + the two
// buckets (top-10 popular, 30 others). Click an option → set cookie
// + navigate to `/{new}/{current_path_without_locale}` + call
// `glmsI18n.changeLanguage(locale)`.
//
// Palette: zero saturated classes. Status indicator (non-default
// locale) uses `border-dashed` not colour · consistent with Wave C.5
// badge weight pattern.
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";

import {
    GLMS_POPULAR, GLMS_REST, LOCALE_COOKIE, LOCALE_COOKIE_TTL,
    normaliseLocale,
} from "@/i18n/wave_d1/glms_locales";
import { glmsI18n, loadGlmsBundle } from "@/i18n/wave_d1/config";
import { isM14LegacyEnabled } from "@/i18n/m14_feature_flag";

function stripLocalePrefix(pathname) {
    const m = pathname.match(/^\/([A-Za-z]{2,3}(?:-[A-Za-z]{2,4})?)(\/|$)/);
    if (!m) return pathname;
    const rest = pathname.slice(1 + m[1].length);
    return rest || "/";
}

export default function LocaleSwitcher() {
    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState(() => {
        try { return normaliseLocale(glmsI18n.language); }
        catch { return "en-US"; }
    });
    const wrapRef = useRef(null);
    const { t } = useTranslation("common", { i18n: glmsI18n });

    // Keep local state in sync with i18n.language changes from other
    // call-sites (e.g. the locale route wrapper on first mount).
    useEffect(() => {
        const onChange = (lng) => setCurrent(normaliseLocale(lng));
        glmsI18n.on("languageChanged", onChange);
        return () => glmsI18n.off("languageChanged", onChange);
    }, []);

    // Close on outside click.
    useEffect(() => {
        if (!open) return;
        function onDocClick(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    async function switchTo(nextLocale) {
        const canonical = normaliseLocale(nextLocale);
        // 1. Persist cookie first — the next page load must boot into
        //    the chosen locale even if the subsequent redirect fails.
        //    Wave D.2 dual-write: ``glms_locale`` (Wave D canonical) +
        //    ``glms_lang`` (M14 legacy compat · 15-locale subset).
        try {
            Cookies.set(LOCALE_COOKIE, canonical, {
                expires: LOCALE_COOKIE_TTL,
                path: "/",
                sameSite: "lax",
                secure: true,
            });
            // Phase II · Wave E.1c.4 · TD6 · M14 dual-write gated.
            // Legacy ``glms_lang`` / localStorage mirror writes only
            // when ``isM14LegacyEnabled()`` is on (default off
            // post-D.6b migration). Rollback: re-arm the window flag.
            if (isM14LegacyEnabled()) {
                const M14_BASES = new Set([
                    "en","it","es","fr","de","pt","nl","pl","tr",
                    "ja","zh-CN","ru","ar","ko","hi",
                ]);
                const m14Base = canonical === "zh-CN"
                    ? "zh-CN"
                    : (canonical.split("-")[0] || canonical);
                if (M14_BASES.has(m14Base)) {
                    Cookies.set("glms_lang", m14Base, {
                        expires: LOCALE_COOKIE_TTL,
                        path: "/",
                        sameSite: "lax",
                        secure: true,
                    });
                    try {
                        if (typeof window !== "undefined" && window.localStorage) {
                            window.localStorage.setItem("glms_lang", m14Base);
                        }
                    } catch (e) { /* storage disabled */ }
                }
            }
        } catch (e) { /* storage disabled */ }
        // 2. Load the namespace lazily, then change language → React
        //    components wired with ``useTranslation({ i18n: glmsI18n })``
        //    re-render automatically.
        try {
            await loadGlmsBundle(canonical, "common");
            await glmsI18n.changeLanguage(canonical);
        } catch (e) { /* fallback stays EN */ }
        // 3. Navigate to the new locale-prefixed URL. We use a full
        //    page reload so the pre-mount basename resolver boots into
        //    the correct basename immediately · zero regression on any
        //    route component that reads the URL at mount.
        if (typeof window !== "undefined" && window.location) {
            const rest = stripLocalePrefix(window.location.pathname);
            const nextPath = `/${canonical}${rest}${window.location.search}${window.location.hash}`;
            window.location.href = nextPath;
        }
        setOpen(false);
    }

    const currentMeta =
        GLMS_POPULAR.concat(GLMS_REST).find((l) => l.code === current)
            || GLMS_POPULAR[0];
    const isNonDefault = current !== "en-US";

    return (
        <div
            ref={wrapRef}
            className="relative inline-block text-left"
            data-testid="locale-switcher"
        >
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                data-testid="locale-switcher-trigger"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={t("locale.switcher_trigger", "Language")}
                className={
                    "inline-flex items-center gap-2 rounded-md border px-3 py-1.5 " +
                    "border-neutral-700 bg-transparent text-sm " +
                    "text-neutral-200 hover:border-neutral-500 transition " +
                    (isNonDefault ? "border-dashed" : "border-solid")
                }
            >
                {isNonDefault && (
                    <span
                        aria-hidden="true"
                        data-testid="locale-non-default-indicator"
                        className="h-2 w-2 rounded-full border border-dashed border-neutral-400"
                        title={t("locale.partial_warning",
                                   "Translations may be partial during Wave D rollout.")}
                    />
                )}
                <span className="font-medium">{current.toUpperCase()}</span>
                <span className="text-neutral-400">·</span>
                <span>{currentMeta.native}</span>
                <svg
                    width="10" height="10" viewBox="0 0 10 10"
                    aria-hidden="true"
                    className={"transition-transform " + (open ? "rotate-180" : "")}
                >
                    <path d="M1 3 L5 7 L9 3" stroke="currentColor" fill="none" strokeWidth="1.4" />
                </svg>
            </button>
            {open && (
                <ul
                    role="listbox"
                    data-testid="locale-switcher-menu"
                    className={
                        "absolute right-0 z-50 mt-2 max-h-96 overflow-y-auto " +
                        "w-72 rounded-md border border-neutral-700 bg-neutral-950 " +
                        "shadow-lg py-2 text-sm"
                    }
                >
                    <li className="px-3 py-1 text-xs uppercase tracking-wider text-neutral-500"
                         data-testid="locale-switcher-group-popular">
                        Popular
                    </li>
                    {GLMS_POPULAR.map((lc) => (
                        <li key={lc.code}>
                            <button
                                type="button"
                                onClick={() => switchTo(lc.code)}
                                data-testid={`locale-switcher-option-${lc.code}`}
                                className={
                                    "w-full text-left px-3 py-1.5 flex items-center " +
                                    "justify-between hover:bg-neutral-900 " +
                                    (lc.code === current ? "bg-neutral-900" : "")
                                }
                            >
                                <span>{lc.native}</span>
                                <span className="text-xs text-neutral-500">{lc.code}</span>
                            </button>
                        </li>
                    ))}
                    <li className="mt-2 px-3 py-1 text-xs uppercase tracking-wider text-neutral-500"
                         data-testid="locale-switcher-group-rest">
                        All languages
                    </li>
                    {GLMS_REST.map((lc) => (
                        <li key={lc.code}>
                            <button
                                type="button"
                                onClick={() => switchTo(lc.code)}
                                data-testid={`locale-switcher-option-${lc.code}`}
                                className={
                                    "w-full text-left px-3 py-1.5 flex items-center " +
                                    "justify-between hover:bg-neutral-900 " +
                                    (lc.code === current ? "bg-neutral-900" : "")
                                }
                            >
                                <span>{lc.native}</span>
                                <span className="text-xs text-neutral-500">{lc.code}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
